import classNames from 'classnames'
import React from 'react'
import { browserHistory } from 'browser/history'

import './_mobile-cac-assignments.scss'

import apis from 'browser/app/models/apis'
import { Collection } from 'shared-libs/models/collection'
import { Query } from 'shared-libs/models/query'
import { Entity } from 'shared-libs/models/entity'
import { CacUtils } from 'shared-libs/helpers/cacUtils'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { TruckLoader } from '../components/truck-loader/truck-loader'

const CAC_ENTITY_TYPE = '/1.0/entities/metadata/core_yms_carrierAppointmentConfirmation.json'

// Active Check-Ins is capped at this many rows; the rest hide behind "See more".
const ACTIVE_MAX_ITEMS = 1

const POLL_INTERVAL_MS = 10000

// Cap the confirmations query so the mobile-web list since the intend in this view is to
// show the most recent confirmations, not to show all of them. 
const QUERY_SIZE = 20

interface IMobileCacAssignmentsState {
  isLoading: boolean
  hasError: boolean
  confirmations: Entity[]
  isActiveExpanded: boolean
}

/**
 * Mobile-web Active Workflows screen — one confirmations source rendered as a
 * single continuous scroll: Active Check-Ins on top capped at one row with an
 * inline "See more (N other active)" toggle (and "See less" to re-collapse),
 * then Upcoming Pre-Checks uncapped below.
 */
export class MobileCacAssignmentsPage extends React.PureComponent<
  {},
  IMobileCacAssignmentsState
> {
  private confirmationsCollection?: Collection
  private pollTimer?: ReturnType<typeof setTimeout>

  constructor(props) {
    super(props)
    this.state = {
      isLoading: true,
      hasError: false,
      confirmations: [],
      isActiveExpanded: false,
    }
  }

  public componentDidMount() {
    void this.load()
    this.scheduleNextPoll()
  }

  public componentWillUnmount() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer)
    }
    this.confirmationsCollection?.dispose()
  }

  // Recursive setTimeout (not setInterval) so the next poll is only scheduled
  // after the current load finishes — avoids overlapping requests on slow
  // connections.
  private scheduleNextPoll() {
    this.pollTimer = setTimeout(async () => {
      await this.load()
      this.scheduleNextPoll()
    }, POLL_INTERVAL_MS)
  }

  public render() {
    const { isLoading, hasError, confirmations, isActiveExpanded } = this.state
    if (isLoading) {
      return <TruckLoader />
    }
    // Only surface an error screen when we have nothing to show — a failed
    // background poll keeps the rows already on screen rather than wiping them.
    if (hasError && confirmations.length === 0) {
      return (
        <div className="mobile-cac-assignments">
          <p className="cac-error">Something went wrong loading your workflows. Please try again.</p>
        </div>
      )
    }
    const active = confirmations.filter((entity) => CacUtils.hasCheckInStarted(entity))
    const upcoming = confirmations.filter((entity) => !CacUtils.hasCheckInStarted(entity))
    const hiddenActiveCount = Math.max(0, active.length - ACTIVE_MAX_ITEMS)
    const visibleActive = isActiveExpanded ? active : active.slice(0, ACTIVE_MAX_ITEMS)

    return (
      <div className="mobile-cac-assignments">
        <section className="cac-section">
          <div className="cac-section__header">
            <span className="cac-section__header-text">Active Check-In</span>
          </div>
          {active.length === 0 ? (
            <p className="cac-section__empty">No active check-ins</p>
          ) : (
            visibleActive.map((entity) => this.renderCard(entity))
          )}
          {hiddenActiveCount > 0 && this.renderSeeMoreToggle(hiddenActiveCount, isActiveExpanded)}
        </section>

        <section className="cac-section">
          <div className="cac-section__header">
            <span className="cac-section__header-text">Upcoming Pre-Checks</span>
          </div>
          {upcoming.length === 0 ? (
            <p className="cac-section__empty">No upcoming pre-checks</p>
          ) : (
            upcoming.map((entity) => this.renderCard(entity))
          )}
        </section>
      </div>
    )
  }

  private renderSeeMoreToggle(hiddenActiveCount: number, isActiveExpanded: boolean) {
    return (
      <div className="cac-see-more">
        <Button
          className={classNames('flex-grow mr3 mobile-button')}
          onClick={this.toggleActiveExpanded}
        >
          {isActiveExpanded ? 'See less' : `See more (${hiddenActiveCount} other active)`}
        </Button>
      </div>
    )
  }

  private toggleActiveExpanded = () => {
    this.setState((prev) => ({ isActiveExpanded: !prev.isActiveExpanded }))
  }

  private renderCard(entity: Entity) {
    const meta = CacUtils.formatAppointmentTime(entity)
    const appointmentNumber = CacUtils.getAppointmentNumber(entity)
    const hasCheckIn = CacUtils.hasCheckInStarted(entity)
    const title = CacUtils.buildTitle(entity)

    return (
      <div
        key={entity.uniqueId}
        className="cac-card"
        onClick={() => browserHistory.push(`/entity/${entity.uniqueId}`)}
      >
        <div className="cac-card__main">
          <div className="cac-card__title">{title}</div>
          {appointmentNumber && <div className="cac-card__sub">TMS ID: {appointmentNumber}</div>}
          <div className="cac-card__meta">{meta}</div>
        </div>
        <div
          className={classNames(
            'cac-pill',
            hasCheckIn ? 'cac-pill--progress' : 'cac-pill--upcoming'
          )}
        >
          {hasCheckIn ? 'In Progress' : 'Upcoming'}
        </div>
      </div>
    )
  }

  // Single fetch path for both the initial load and each poll. isLoading only
  // ever goes false here (set true once in the constructor for first paint), so
  // background polls refresh silently without flashing the loader.
  private async load() {
    try {
      const confirmations = await this.fetchConfirmations()
      this.setState({ confirmations, hasError: false, isLoading: false })
    } catch {
      console.error('Failed to load CAC assignments')
      this.setState({ hasError: true, isLoading: false })
    }
  }

  private async fetchConfirmations(): Promise<Entity[]> {
    this.confirmationsCollection?.dispose()
    const query = new Query(apis)
      .setEntityType(CAC_ENTITY_TYPE)
      .setOrders([{ path: 'creationDate', type: 'descending' }])
      .setSize(QUERY_SIZE)
      .setShouldIncludeLeafEntities(true)
    this.confirmationsCollection = new Collection(apis, query)
    return this.confirmationsCollection.find()
  }
}
